import { useEffect, useMemo, useState } from "react";
import { adminApi } from "@/services/adminApi";
import { Plug, Loader2, Check, X, Search } from "lucide-react";
import { toast } from "sonner";

type ConnectorMeta = {
  type: string;
  label: string;
  description: string;
  category: string;
};

// Mirrors the connector list and category mapping from the user-facing
// /settings?tab=connectors page. Keep in sync when new connectors ship.
const CONNECTOR_META: ConnectorMeta[] = [
  { type: "googleDrive", label: "Google Workspace", description: "Drive, Sheets, Docs, Slides", category: "File Storage" },
  { type: "googleGmail", label: "Gmail", description: "Email search and retrieval", category: "Email & Communication" },
  { type: "googleCalendar", label: "Google Calendar", description: "Calendar events", category: "Productivity & Collaboration" },
  { type: "googleChat", label: "Google Chat", description: "Workspace spaces and messages", category: "Productivity & Collaboration" },
  { type: "slack", label: "Slack", description: "Workspace messages and channels", category: "Productivity & Collaboration" },
  { type: "discord", label: "Discord", description: "Servers and channels", category: "Productivity & Collaboration" },
  { type: "linear", label: "Linear", description: "Issues and projects", category: "Productivity & Collaboration" },
  { type: "asana", label: "Asana", description: "Tasks and projects", category: "Productivity & Collaboration" },
  { type: "jira", label: "Jira", description: "Projects and tasks", category: "Productivity & Collaboration" },
  { type: "microsoftTeams", label: "Microsoft Teams", description: "Channels and chats", category: "Productivity & Collaboration" },
  { type: "microsoft365", label: "Microsoft 365", description: "Unified search across M365", category: "Productivity & Collaboration" },

  { type: "dropbox", label: "Dropbox", description: "Files and folders", category: "File Storage" },
  { type: "oneDrive", label: "OneDrive", description: "Files and folders", category: "File Storage" },
  { type: "docusign", label: "DocuSign", description: "Envelopes and templates", category: "File Storage" },

  { type: "outlook", label: "Outlook", description: "Email and calendar", category: "Email & Communication" },
  { type: "freshchat", label: "Freshchat", description: "Conversations and users", category: "Email & Communication" },

  { type: "postgres", label: "Postgres", description: "Postgres database", category: "Data & Analytics" },
  { type: "mysql", label: "MySQL", description: "MySQL database", category: "Data & Analytics" },
  { type: "airtable", label: "Airtable", description: "Bases and tables", category: "Data & Analytics" },
  { type: "amplitude", label: "Amplitude", description: "Product analytics", category: "Data & Analytics" },
  { type: "datadog", label: "Datadog", description: "Metrics and logs", category: "Data & Analytics" },

  { type: "salesforce", label: "Salesforce", description: "CRM data", category: "CRM & Sales" },
  { type: "hubspot", label: "HubSpot", description: "CRM data", category: "CRM & Sales" },
  { type: "pipedrive", label: "Pipedrive", description: "CRM data", category: "CRM & Sales" },
  { type: "freshdesk", label: "Freshdesk", description: "Tickets and customers", category: "CRM & Sales" },
  { type: "zendesk", label: "Zendesk", description: "Tickets and customers", category: "CRM & Sales" },
  { type: "intercom", label: "Intercom", description: "Customer messaging", category: "CRM & Sales" },
  { type: "crisp", label: "Crisp", description: "Chat conversations", category: "CRM & Sales" },

  { type: "stripe", label: "Stripe", description: "Payments and customers", category: "Finance & Accounting" },
  { type: "plaid", label: "Plaid", description: "Banking data", category: "Finance & Accounting" },

  { type: "notion", label: "Notion", description: "Pages and databases", category: "Knowledge Bases" },
  { type: "confluence", label: "Confluence", description: "Spaces and pages", category: "Knowledge Bases" },

  { type: "github", label: "GitHub", description: "Repositories and issues", category: "Engineering & DevOps" },
  { type: "bitbucket", label: "Bitbucket", description: "Repos, PRs, commits", category: "Engineering & DevOps" },
  { type: "sentry", label: "Sentry", description: "Issues and events", category: "Engineering & DevOps" },
  { type: "newrelic", label: "New Relic", description: "APM and monitoring", category: "Engineering & DevOps" },
  { type: "opsgenie", label: "OpsGenie", description: "Alerts and on-call", category: "Engineering & DevOps" },

  { type: "figma", label: "Figma", description: "Files and components", category: "Design & Product" },
  { type: "miro", label: "Miro", description: "Boards and items", category: "Design & Product" },
  { type: "lucid", label: "Lucid", description: "Diagrams and folders", category: "Design & Product" },

  { type: "bamboohr", label: "BambooHR", description: "People and time off", category: "HR & People Ops" },
  { type: "gusto", label: "Gusto", description: "Payroll and benefits", category: "HR & People Ops" },
  { type: "deel", label: "Deel", description: "Contractor management", category: "HR & People Ops" },
  { type: "ashby", label: "Ashby", description: "Candidates and jobs", category: "HR & People Ops" },
  { type: "hellosign", label: "HelloSign", description: "E-signatures", category: "HR & People Ops" },

  { type: "fireflies", label: "Fireflies", description: "Meeting recordings", category: "Meetings" },
];

const CATEGORY_ORDER = [
  "Productivity & Collaboration",
  "Email & Communication",
  "File Storage",
  "CRM & Sales",
  "Data & Analytics",
  "Engineering & DevOps",
  "Knowledge Bases",
  "Design & Product",
  "HR & People Ops",
  "Finance & Accounting",
  "Meetings",
  "Other",
];

export default function ConnectorFlagsPage() {
  const [loading, setLoading] = useState(true);
  const [disabledSet, setDisabledSet] = useState<Set<string>>(new Set());
  const [allTypes, setAllTypes] = useState<string[]>([]);
  const [pending, setPending] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    adminApi
      .getConnectorFlags()
      .then(({ disabledConnectorTypes, allConnectorTypes }) => {
        setDisabledSet(new Set(disabledConnectorTypes));
        setAllTypes(allConnectorTypes);
      })
      .catch((e: any) =>
        toast.error(
          e?.response?.data?.error ||
            e?.message ||
            "Failed to load connector flags",
        ),
      )
      .finally(() => setLoading(false));
  }, []);

  const handleToggle = async (type: string) => {
    const isCurrentlyDisabled = disabledSet.has(type);
    const next = new Set(disabledSet);
    if (isCurrentlyDisabled) next.delete(type);
    else next.add(type);

    setPending(type);
    try {
      const { disabledConnectorTypes } = await adminApi.updateConnectorFlags(
        Array.from(next),
      );
      const updated = new Set(disabledConnectorTypes);
      setDisabledSet(updated);
      const meta = CONNECTOR_META.find(m => m.type === type);
      toast.success(
        `${meta?.label || type} ${updated.has(type) ? "disabled" : "enabled"}`,
      );
    } catch (e: any) {
      toast.error(
        e?.response?.data?.error ||
          e?.message ||
          `Failed to update ${type}`,
      );
    } finally {
      setPending(null);
    }
  };

  // Merge backend-known types with metadata so we always render every
  // ConnectorType the backend exposes, even ones we haven't added to
  // CONNECTOR_META yet (renders with a fallback label).
  const rows = useMemo(() => {
    const known = new Map(CONNECTOR_META.map(m => [m.type, m]));
    const merged: ConnectorMeta[] = allTypes.map(t => {
      const meta = known.get(t);
      if (meta) return meta;
      return {
        type: t,
        label: t,
        description: "(no description — add metadata in ConnectorFlagsPage.tsx)",
        category: "Other",
      };
    });

    const filtered = search.trim()
      ? merged.filter(m => {
          const q = search.trim().toLowerCase();
          return (
            m.label.toLowerCase().includes(q) ||
            m.type.toLowerCase().includes(q) ||
            m.description.toLowerCase().includes(q) ||
            m.category.toLowerCase().includes(q)
          );
        })
      : merged;

    const grouped = new Map<string, ConnectorMeta[]>();
    for (const m of filtered) {
      const arr = grouped.get(m.category) || [];
      arr.push(m);
      grouped.set(m.category, arr);
    }
    // Sort entries inside each category alphabetically by label.
    for (const arr of grouped.values()) arr.sort((a, b) => a.label.localeCompare(b.label));

    const orderedCategories = [
      ...CATEGORY_ORDER.filter(c => grouped.has(c)),
      ...[...grouped.keys()].filter(c => !CATEGORY_ORDER.includes(c)).sort(),
    ];
    return orderedCategories.map(c => ({ category: c, items: grouped.get(c)! }));
  }, [allTypes, search]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  const enabledCount = allTypes.length - disabledSet.size;

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Plug className="h-6 w-6 text-blue-600" />
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              Connector Flags
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Global per-connector kill switches. A disabled connector is hidden from the
              /settings UI, blocked from new OAuth, skipped by the sync worker, and
              removed from the agent&apos;s tool surface &middot; {enabledCount}/{allTypes.length} enabled
            </p>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Filter by name, type, or category"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Categories */}
      {rows.map(({ category, items }) => (
        <div
          key={category}
          className="border border-gray-200 rounded-lg overflow-hidden text-sm"
        >
          <div className="grid grid-cols-[1fr_120px] bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
            <div className="px-4 py-2.5">{category}</div>
            <div className="px-4 py-2.5 border-l border-gray-200 text-center">
              Status
            </div>
          </div>

          {items.map(meta => {
            const isOn = !disabledSet.has(meta.type);
            const isPending = pending === meta.type;
            return (
              <div
                key={meta.type}
                className="grid grid-cols-[1fr_120px] border-b border-gray-100 last:border-b-0 hover:bg-gray-50/50 transition-colors"
              >
                <div className="px-4 py-3.5">
                  <div className="flex items-center gap-2">
                    <span
                      className={`inline-block w-2 h-2 rounded-full shrink-0 ${
                        isOn ? "bg-green-500" : "bg-gray-300"
                      }`}
                    />
                    <span className="text-sm font-medium text-gray-900">
                      {meta.label}
                    </span>
                    <span className="text-xs text-gray-400 font-mono">
                      {meta.type}
                    </span>
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5 ml-4">
                    {meta.description}
                  </p>
                </div>

                <div className="flex items-center justify-center border-l border-gray-100">
                  <button
                    type="button"
                    disabled={pending !== null}
                    onClick={() => handleToggle(meta.type)}
                    className={`
                      inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold tracking-wide
                      transition-all duration-150 cursor-pointer
                      disabled:opacity-50 disabled:cursor-not-allowed
                      ${
                        isOn
                          ? "bg-green-50 text-green-700 hover:bg-green-100"
                          : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                      }
                    `}
                  >
                    {isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : isOn ? (
                      <Check className="h-3.5 w-3.5" />
                    ) : (
                      <X className="h-3.5 w-3.5" />
                    )}
                    {isOn ? "ON" : "OFF"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
